import styled from '@emotion/styled';
import { fromTheme } from '@kyruus/ui-theme';
import Star from '@kyruus/react-ikons/Star';
import StarOutline from '@kyruus/react-ikons/StarOutline';
import { BREAKPOINTS, RATINGS_AND_REVIEWS } from '../../lib/constants';

const { STAR_INNER_MARGIN } = RATINGS_AND_REVIEWS;

export const ReviewsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${fromTheme('spacing_medium')};
`;

export const ReviewsTitle = styled.h2`
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size_heading_4')};
  font-weight: ${fromTheme('font_weight_bolder')};
  margin: 0;
`;

export const RatingsReviewsCount = styled.span`
  color: ${fromTheme('color_text_secondary')};
  font-size: ${fromTheme('font_size')};
  align-self: center;
`;

export const ReviewsDisclaimer = styled.p`
  color: ${fromTheme('color_text_secondary')};
  font-size: ${fromTheme('font_size')};
  margin: ${fromTheme('spacing_medium')} 0;
  line-height: ${fromTheme('font_line_height')};
`;

export const ReviewsSearchInputTitle = styled.strong`
  display: block;
  color: ${fromTheme('color_text_secondary')};
  font-size: ${fromTheme('font_size')};
  line-height: ${fromTheme('font_line_height')};
  margin-top: ${fromTheme('spacing_large')};
  margin-bottom: ${fromTheme('spacing_xsmall')};
  font-weight: ${fromTheme('font_weight_bold')};
`;

export const SearchInputContainer = styled.div`
  position: relative;
  max-width: 290px;

  label {
    padding: 0;
  }

  input {
    padding-left: ${fromTheme('spacing_small')};
    padding-right: ${fromTheme('spacing_large')};
    border-radius: ${(props) =>
      fromTheme('provider_profile_border_radius')(props)};

    // prevent borders from being cut off because outlines are drawn outside of the elements border
    // and outline is not a part of an element's dimensions
    margin-left: ${fromTheme('border_width')};
  }

  @media print {
    display: none;
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  pointer-events: none;
  top: 50%;
  transform: translateY(-50%);
  right: ${fromTheme('spacing_small')};
  color: ${fromTheme('color_info')};

  svg path {
    fill: ${fromTheme('color_text_secondary')};
  }
`;

export const CancelButton = styled('button')`
  position: absolute;
  padding: ${fromTheme('spacing_small')};
  appearance: none;
  border: 0;
  margin: 0;
  background: transparent;
  top: 2px;
  right: 0;
  cursor: pointer;
  outline: 0;

  svg path {
    fill: ${fromTheme('color_text_secondary')};
  }

  &:focus {
    outline-color: ${fromTheme('color_link')};
    outline-width: ${fromTheme('border_width')};
    outline-style: solid;
    outline-offset: 0px;
    border-radius: ${fromTheme('provider_profile_border_radius')};
  }
`;

// ------------------------------------------
// Ratings Section
// ------------------------------------------
export const RatingsSection = styled.div``;

// Average Rating
export const AverageRatingContainer = styled.div`
  display: flex;
  margin-top: ${fromTheme('spacing_medium')};
  gap: ${fromTheme('spacing_small')};
  line-height: ${fromTheme('spacing_medium')};
`;

type SummaryStarsProps = {
  starSize: number;
  ratingScale: number;
  widthAccuracyCorrection: number;
};

export const SummaryStars = styled.div<SummaryStarsProps>`
  // starSize by ratingScale (number of max stars)
  // add margin for all stars except last one
  // add correction factor to make the star rating display accurately
  width: calc(
    ${(props) => {
      const { starSize, ratingScale, widthAccuracyCorrection } = props;

      return `${starSize}px * ${ratingScale} +
      ${STAR_INNER_MARGIN}px * (${ratingScale} - 1) -
      ${widthAccuracyCorrection}px`;
    }}
  );
  @media screen and (max-width: ${BREAKPOINTS.small}px) {
    display: none;
  }
`;

export const StarWrapper = styled.div`
  position: relative;
  width: 100%;
  svg {
    color: ${fromTheme('color_star_ratings')};
  }
`;

export const EmptyStars = styled.div`
  position: absolute;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
`;

type FilledStarsProps = {
  filledPercent: string;
};

export const FilledStars = styled.div<FilledStarsProps>`
  position: absolute;
  width: ${(props) => props.filledPercent};
  white-space: nowrap;
  overflow: hidden;
`;

export const StyledStarOutline = styled(StarOutline)`
  :not(:last-child) {
    margin-right: ${STAR_INNER_MARGIN}px;
  }
`;

export const StyledStar = styled(Star)`
  :not(:last-child) {
    margin-right: ${STAR_INNER_MARGIN}px;
  }
`;

export const AverageRatingHeading = styled.h3`
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size')};
  font-weight: ${fromTheme('font_weight_bolder')};
  margin: 0;
`;

// Sub Ratings
export const SubRatingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${fromTheme('spacing_small')};
  margin: ${fromTheme('spacing_medium')} 0 0;
`;

export const SubRatingRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${fromTheme('color_text')};
  align-items: center;
`;

export const RatingTitle = styled.span`
  font-size: ${fromTheme('font_size')};
  font-weight: ${fromTheme('font_weight_bold')};
`;

export const RatingStarValue = styled.div`
  display: flex;
  gap: ${fromTheme('spacing_small')};
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size')};
  border-radius: ${fromTheme('border_radius_rounded')};
  padding: 2px 2px 2px 12px;
`;

export const IndividualReviewRatings = styled.div`
  color: ${fromTheme('color_text')};
  font-size: ${fromTheme('font_size')};
  font-weight: ${fromTheme('font_weight')};
  flex-grow: 1;
  margin: 0;
  padding-left: ${fromTheme('spacing_small')};

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

export const AvgMetricsReviewRatings = styled(IndividualReviewRatings)`
  padding-left: 0;
`;
